import React, { useEffect, useMemo, useState } from "react";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import CollapsibleCard from "components/CollapsibleCard";
import { del, get, post, request } from "../../../../requests";
import InnerTable from "lib/Tables/InnerTable";

import MDTypography from "../../../../components/MDTypography";
import Alert from "@mui/material/Alert";
import { formatCurrencyUSD } from "utils/CommonUtils";
import { useStoreActions, useStoreState } from "easy-peasy";
import CollapsiblePivotTable from "lib/Tables/PivotTable/CollapsiblePivotTable";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import moment from "moment";

interface PositionData {
  company?: string;
  withdrawalcount?: number;
  withdrawaltotal?: string;
  withdrawalfrequency?: string;
  lastwithdrawaldate?: string;
  lastwithdrawalamount?: string;
  depositcount?: number;
  deposittotal?: string;
  withholdpercent?: string;
  term?: string;
  months?: Array<{
    month?: string;
    workdays?: number;
    withdrawalcount?: number;
    withdrawaltotal?: string;
    withholdpercent?: string;
    withdrawalfrequency?: string;
    lastwithdrawaldate?: string;
    lastwithdrawalamount?: string;
    deposittotal?: string;
    depositdate?: string;
    depositcount?: number;
    term?: string;
  }>;
  financings?: any[];
}

interface Position {
  id: number | string;
  funder: string;
  status: string;
  type: string;
  frequency: string;
  debitAmount: number;
  weeklyAmount: number;
  monthlyAmount: number;
  fundedDate: string;
  fundedAmount: number;
  lastPayment: string;
  noOfDebits: number | string;
  leverage: string;
  estTerm: string;
  estBalance: number;
  source: "MoneyThumb" | "Internal" | "AI";
  rawData?: PositionData | ApiPositionData;
}

interface ApiPositionData {
  id: number;
  dealApplicationId: number;
  funderName: string;
  summaryJson: string;
  createdAt: string;
  updatedAt: string;
}

interface AdvancesPositionsProps {
  dealApplicationId: any;
  setComponentLoading?: (componentName: string, isLoading: boolean) => void;
  source: "smartScrub" | "lendPathway" | "moneyThumb";

}

const RecurringPayments: React.FC<AdvancesPositionsProps> = ({
  dealApplicationId,
  setComponentLoading,
  source
}) => {
  const [editingRow, setEditingRow] = useState<any | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [sortedColumn, setSortedColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [showEditWarning, setShowEditWarning] = useState(false);
  const [pendingEditRow, setPendingEditRow] = useState<Position | null>(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);

  const advancesData = useStoreState((state: any) => state.bankSummaryStore.advancesData);
  const setAdvancesData = useStoreActions(
    (actions: any) => actions.bankSummaryStore.setAdvancesData
  );
  const bankSummaryData = useStoreState((state: any) => state.bankSummaryStore.bankSummaryData);

  useEffect(() => {
    fetchAdvancesData();
  }, [dealApplicationId]);

  const filteredAdvancesData = useMemo(() => {
    return advancesData?.filter((row: any) => Number(row?.monthlyAmount) > 0);
  }, [advancesData]);

  const fetchAdvancesData = async () => {
    try {
      setComponentLoading("advancesPositions", true);
      const response = await get(`/bankStatements/getAllAdvances/${dealApplicationId}`);

      if (Array.isArray(response)) {
        let filteredData = response;

        if (source === "smartScrub") {
          const allowedSources = ["ocr", "plaid", "internal", "ai"];
          filteredData = filteredData.filter((row: any) =>
            allowedSources.includes((row.source || "").toLowerCase())
          );
        } else if (source === "moneyThumb") {
          filteredData = filteredData.filter(
            (row: any) => (row.source || "").toLowerCase() === "moneythumb"
          );
        }

        const dataWithSelection = filteredData.map((row) => ({
          ...row,
          selected: row.status === "Active",
        }));
        setAdvancesData(dataWithSelection);

        const activeRowIds = dataWithSelection
          .filter((row) => row.status === "Active" && row?.monthlyAmount && row.monthlyAmount > 0)
          .map((row) => row.id);
        setSelectedRows(activeRowIds);
      } else {
        setAdvancesData([]);
        console.warn("Unexpected advances response:", response);
      }
    } catch (error) {
      console.error("Error fetching advances:", error);
      setAdvancesData([]);
    } finally {
      setComponentLoading("advancesPositions", false);
    }
  };

  const handleAddPosition = () => {
    const newPosition = {
      id: `new-${Date.now()}`,
      funder: "",
      status: "Active",
      type: "Loan",
      frequency: "Monthly",
      debitAmount: 0,
      weeklyAmount: 0,
      monthlyAmount: 0,
      fundedDate: new Date().toISOString().split("T")[0],
      fundedAmount: 0,
      lastPayment: "",
      numberOfDebits: 0,
      mcaHoldback: 0,
      estimatedTerm: 0,
      estimatedBalance: 0,
      source: source === "moneyThumb" ? "moneyThumb" : "Internal",
    };
    setEditingRow(newPosition);
    setIsEditing(true);
    setOpenAddDialog(true);
  };

  const handleDeletePosition = async (id: string | number) => {
    if (!id) return;
    try {
      const positionToDelete = advancesData.find((position: any) => position?.id === id);
      if (!positionToDelete) {
        console.error("Position not found");
        return;
      }

      setShowDeleteAlert(true);
      setPendingEditRow(positionToDelete);
      return;
    } catch (error) {
      console.error("Error deleting position:", error);
      alert("Failed to delete position. Please try again.");
    }
  };

  const handleSavePosition = async (position: any) => {
    try {
      if (typeof position.id === "string" && position.id.toLowerCase().startsWith("new")) {
        position.id = null;
      }
      if (!position.dealApplicationId) {
        position.dealApplicationId = dealApplicationId;
      }

      const payload = position;

      const response = await post("/bankStatements/saveOrUpdateAdvance", payload);
      await fetchAdvancesData();
      setIsEditing(false);
      setEditingRow(null);
    } catch (error) {
      console.error("Error saving position:", error);
      alert("Failed to save position. Please try again.");
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingRow(null);
  };

  const handleEditPosition = (row: any) => {
    if (row.source === "MoneyThumb" || row.source === "AI") {
      setPendingEditRow(row);
      setShowEditWarning(true);
      return;
    }

    setEditingRow({ ...row });
    setIsEditing(true);
  };

  const handleInputChange = (value: any, field: string) => {
    setEditingRow((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSelectRow = (rowKey: any) => {
    const updated = advancesData.map((row: any) => {
      if (row.id === rowKey && Number(row?.monthlyAmount) > 0) {
        return { ...row, selected: !row.selected };
      }
      return row;
    });

    setAdvancesData(updated);

    const updatedSelectedRows = updated
      .filter((row: any) => Number(row?.monthlyAmount) > 0 && row.selected)
      .map((row: any) => row.id);

    setSelectedRows(updatedSelectedRows);
  };

  const handleSelectAll = () => {
    const allSelected = filteredAdvancesData.every((row: any) => row.selected);

    const updated = advancesData.map((row: any) => {
      if (Number(row?.monthlyAmount) > 0) {
        return {
          ...row,
          selected: !allSelected,
        };
      }
      return row;
    });

    setAdvancesData(updated);

    const updatedSelectedRows = !allSelected
      ? updated
          .filter((row: any) => Number(row?.monthlyAmount) > 0 && row.selected)
          .map((row: any) => row.id)
      : [];

    setSelectedRows(updatedSelectedRows);
  };

  const handleSort = (columnKey: string) => {
    const isAsc = sortedColumn === columnKey && sortDirection === "asc";
    setSortedColumn(columnKey);
    setSortDirection(isAsc ? "desc" : "asc");
  };

  const getRowStyle = (row: any) => {
    return row.status === "Active" ? "bold" : "regular";
  };

  const isActionsDisabled = (row: any) => {
    return false;
  };

  const columns = [
    {
      Header: "FUNDER",
      accessor: "funder",
      align: "left",
      width: "200px",
    },
    {
      Header: "STATUS",
      accessor: "status",
      input: "select",
      align: "center",
      width: "80px",
      options: [
        { label: "Active", value: "Active" },
        { label: "Inactive", value: "Inactive" },
      ],
    },
    {
      Header: "FREQUENCY",
      accessor: "frequency",
      width: "80px",
      input: "select",
      options: [
        { label: "Daily", value: "Daily" },
        { label: "Weekly", value: "Weekly" },
        { label: "Monthly", value: "Monthly" },
      ],
    },
    {
      Header: "DEBIT AMOUNT",
      accessor: "debitAmount",
      input: "amount",
      width: "90px",
      align: "left",
      Cell: (row: any) => formatCurrencyUSD(row?.row?.debitAmount),
    },
    {
      Header: "MONTHLY AMOUNT",
      accessor: "monthlyAmount",
      width: "110px",
      input: "amount",
      align: "left",
      Cell: (row: any) => formatCurrencyUSD(row?.row?.monthlyAmount),
    },
    {
      Header: "LAST PAYMENT",
      accessor: "lastPayment",
      input: "date",
      width: "120px",
    },
    {
      Header: "NO. OF DEBITS",
      accessor: "numberOfDebits",
      input: "number",
      align: "center",
      width: "80px",
    },
  ];

  const renderTotal = (rows: any[], key: string) => {
    let total = 0;

    if (key === "mcaHoldback") {
      let totalMcaDebits = 0;
      let totalTrueCredits = 0;

      rows.forEach((row: any) => {
        if (row.selected) {
          totalMcaDebits += parseFloat(row["totalMcaDebits"] || "0") || 0;
          totalTrueCredits += parseFloat(row["totalTrueCredits"] || "0") || 0;
        }
      });

      total = totalTrueCredits ? (totalMcaDebits / totalTrueCredits) * 100 : 0;
      return `${total.toFixed(2)}%`;
    }

    rows.forEach((row: any) => {
      if (row.selected && row[key]) {
        total += parseFloat(row[key]) || 0;
      }
    });

    return key.includes("Amount")
      ? `${formatCurrencyUSD(total.toFixed(2))}`
      : key.includes("mcaHoldback")
      ? `${total.toFixed(2)}%`
      : `${formatCurrencyUSD(total.toFixed(2))}`;
  };

  const customButton = (
    <MDButton
      color="primary"
      variant="contained"
      size="medium"
      sx={{ textTransform: "none" }}
      onClick={handleAddPosition}
    >
      + Add Recurring Debit
    </MDButton>
  );

  return (
    <CollapsibleCard header={"Recurring Debits"} customButton={customButton}>
      <MDBox paddingRight={3}>
        <CollapsiblePivotTable
          columns={columns}
          rowKey="id"
          data={filteredAdvancesData || []}
          onDelete={handleDeletePosition}
          onEdit={handleEditPosition}
          onSave={handleSavePosition}
          onCancel={handleCancelEdit}
          editingRow={editingRow}
          isEditing={isEditing}
          onInputChange={handleInputChange}
          selectedRows={selectedRows}
          onSelectRow={handleSelectRow}
          onSelectAll={handleSelectAll}
          onSort={handleSort}
          sortedColumn={sortedColumn}
          sortDirection={sortDirection}
          getRowStyle={getRowStyle}
          isActionsDisabled={isActionsDisabled}
          renderTotal={renderTotal}
          totalColumn={["monthlyAmount"]}
          groupBy="lastPayment"
          groupFormatter={(value) => {
            if (!value) return "Unknown";
            if (moment(value, moment.ISO_8601, true).isValid()) {
              return moment(value).format("MMMM YYYY");
            }
            return String(value);
          }}
          defaultExpanded={false}
        />
      </MDBox>

      {showEditWarning && (
        <MDBox
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <MDBox
            sx={{
              backgroundColor: "background.paper",
              padding: 3,
              borderRadius: 1,
              maxWidth: 600,
            }}
          >
            <MDTypography variant="h6" mb={2}>
              <Alert variant="filled" severity="warning">
                Warning
              </Alert>
            </MDTypography>
            <MDTypography mb={2}>
              Editing an Recurring Debits may cause misalignment with the Debits in Bank Statements
              Summary. Your Total Monthly Amount here will be considered as Total MCA Debits on the
              scorecard.
              <br />
              <br />
              Do you still want to continue?
            </MDTypography>
            <MDBox display="flex" justifyContent="flex-end" gap={2}>
              <MDButton
                variant="contained"
                color="primary"
                onClick={() => {
                  if (pendingEditRow) {
                    setEditingRow({ ...pendingEditRow });
                    setIsEditing(true);
                  }
                  setShowEditWarning(false);
                }}
              >
                Yes, Continue
              </MDButton>
              <MDButton
                variant="outlined"
                color="secondary"
                onClick={() => setShowEditWarning(false)}
              >
                No, Cancel
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      )}

      {showDeleteAlert && (
        <MDBox
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
          onClick={() => {
            setShowDeleteAlert(false);
            setPendingEditRow(null);
          }}
        >
          <MDBox
            sx={{
              backgroundColor: "background.paper",
              borderRadius: 2,
              maxWidth: 600,
              textAlign: "center",
              border: "3px solid",
              borderColor: "gray",
              boxShadow: 24,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <MDTypography variant="h6" mb={2}>
              <Alert
                severity="info"
                sx={{
                  "& .MuiAlert-message": {
                    fontSize: "18px",
                    fontWeight: "bold",
                    marginTop: "1rem",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  },
                }}
              >
                Deleting an Recurring Payments may cause misalignment with the Debits in Bank
                Statements Summary. Your Total Monthly Amount here will be considered as{" "}
                <em> Total MCA Debits</em> on the scorecard.
                <MDBox width="100%" display="flex" justifyContent="center" gap={2} marginTop={3}>
                  <MDButton
                    variant="contained"
                    color="error"
                    onClick={async () => {
                      if (pendingEditRow && pendingEditRow.id) {
                        await del(`/bankStatements/deleteAdvances/${pendingEditRow.id}`);
                        fetchAdvancesData();
                      }
                      setShowDeleteAlert(false);
                      setPendingEditRow(null);
                    }}
                  >
                    Delete
                  </MDButton>
                  <MDButton
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      setPendingEditRow(null);
                      setShowDeleteAlert(false);
                    }}
                  >
                    Cancel
                  </MDButton>
                </MDBox>
              </Alert>
            </MDTypography>
          </MDBox>
        </MDBox>
      )}

      <Dialog
        open={openAddDialog}
        onClose={() => {
          setOpenAddDialog(false);
          setEditingRow(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Recurring Debit</DialogTitle>
        <DialogContent>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              <tr>
                <td style={{ padding: "10px", width: "30%" }}>
                  <strong>Funder</strong>
                </td>
                <td style={{ padding: "10px" }}>
                  <TextField
                    fullWidth
                    value={editingRow?.funder || ""}
                    onChange={(e) => handleInputChange(e.target.value, "funder")}
                  />
                </td>
              </tr>

              <tr>
                <td style={{ padding: "10px" }}>
                  <strong>Status</strong>
                </td>
                <td style={{ padding: "10px" }}>
                  <Select
                    fullWidth
                    sx={{ height: "30px" }}
                    value={editingRow?.status || ""}
                    onChange={(e) => handleInputChange(e.target.value, "status")}
                    endAdornment={<ExpandMore />}
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </td>
              </tr>

              <tr>
                <td style={{ padding: "10px" }}>
                  <strong>Frequency</strong>
                </td>
                <td style={{ padding: "10px" }}>
                  <Select
                    fullWidth
                    sx={{ height: "30px" }}
                    value={editingRow?.frequency || ""}
                    onChange={(e) => handleInputChange(e.target.value, "frequency")}
                    endAdornment={<ExpandMore />}
                  >
                    <MenuItem value="Daily">Daily</MenuItem>
                    <MenuItem value="Weekly">Weekly</MenuItem>
                    <MenuItem value="Monthly">Monthly</MenuItem>
                  </Select>
                </td>
              </tr>

              <tr>
                <td style={{ padding: "10px" }}>
                  <strong>Debit Amount</strong>
                </td>
                <td style={{ padding: "10px" }}>
                  <TextField
                    fullWidth
                    type="number"
                    value={editingRow?.debitAmount || ""}
                    onChange={(e) => handleInputChange(Number(e.target.value), "debitAmount")}
                  />
                </td>
              </tr>

              <tr>
                <td style={{ padding: "10px" }}>
                  <strong>Monthly Amount</strong>
                </td>
                <td style={{ padding: "10px" }}>
                  <TextField
                    fullWidth
                    type="number"
                    value={editingRow?.monthlyAmount || ""}
                    onChange={(e) => handleInputChange(Number(e.target.value), "monthlyAmount")}
                  />
                </td>
              </tr>

              <tr>
                <td style={{ padding: "10px" }}>
                  <strong>Last Payment</strong>
                </td>
                <td style={{ padding: "10px" }}>
                  <TextField
                    fullWidth
                    type="date"
                    value={editingRow?.lastPayment || ""}
                    onChange={(e) => handleInputChange(e.target.value, "lastPayment")}
                    InputLabelProps={{ shrink: true }}
                  />
                </td>
              </tr>

              <tr>
                <td style={{ padding: "10px" }}>
                  <strong>No. of Debits</strong>
                </td>
                <td style={{ padding: "10px" }}>
                  <TextField
                    fullWidth
                    type="number"
                    value={editingRow?.numberOfDebits || ""}
                    onChange={(e) => handleInputChange(Number(e.target.value), "numberOfDebits")}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </DialogContent>

        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenAddDialog(false);
              handleSavePosition(editingRow);
            }}
            variant="contained"
            color="primary"
          >
            Save
          </MDButton>
          <MDButton
            onClick={() => {
              setOpenAddDialog(false);
              setEditingRow(null);
            }}
            variant="outlined"
            color="secondary"
          >
            Cancel
          </MDButton>
        </DialogActions>
      </Dialog>
    </CollapsibleCard>
  );
};

export default RecurringPayments;
